import Highcharts, { Point } from 'highcharts';
import SankeyModule from 'highcharts/modules/sankey';
import { merge } from 'lodash';
import { DefaultTheme } from 'styled-components';
import { getDimensions } from '@kentik/ui-shared/query/query';
import { getMeasurementDimension, getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import {
  isPathDimension,
  shortenDimensionValue,
  splitPathDimensionValue
} from '@kentik/ui-shared/query/dimensionUtils';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import QueryModel from 'app/stores/ude/QueryModel';
import { getBaseChartStyle, getRowDimensionValues } from 'app/views/ude/dataviews/util/highcharts.util';
import { DimensionFormatter, HighchartsOptionsResult } from 'app/views/ude/dataviews/util/highcharts';
import { formatMetricValue, getDatasetPrefix } from './unitFormatter';

SankeyModule(Highcharts);

declare module 'highcharts' {
  interface HighchartsCustom {
    rowIds?: string[];
  }
}

const MIN_LINK_WIDTH = 2;
const NODE_WIDTH = 20;
const LABEL_OFFSET = 6;
const LINK_OPACITY = 0.12;
const LINK_HOVER_OPACITY = 0.45;

interface SankeyPoint extends Point {
  isNode?: boolean;
  sum?: number;
  linksFrom?: Point[];
  linksTo?: Point[];
  id?: string;
  from?: string;
  to?: string;
  weight?: number;
}

interface SankeyColumn {
  dimensionName: string;
  label: string;
}

function buildColumns(
  dimensions: { name: string }[],
  rowTokens: string[][][],
  measurement: Measurement
): SankeyColumn[] {
  return dimensions.flatMap((dimension, dimensionIdx) => {
    const label = getMeasurementDimension(measurement, dimension.name)?.Label ?? dimension.name;

    if (!isPathDimension(dimension.name)) {
      return [{ dimensionName: dimension.name, label }];
    }

    const maxHops = rowTokens.reduce((max, tokens) => Math.max(max, tokens[dimensionIdx].length), 1);
    return Array.from({ length: maxHops }, (_, hop) => ({
      dimensionName: dimension.name,
      label: `${label} Step ${hop + 1}`
    }));
  });
}

export function generateSankeyOptions(
  queryModel: QueryModel,
  theme: DefaultTheme,
  metricName: string,
  rollupName: string,
  measurement: Measurement,
  dimensionFormatter?: DimensionFormatter
): HighchartsOptionsResult {
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const unit = measurementMetric?.Unit;
  const dimensions = getDimensions(queryModel.query);
  const visualizationRows = queryModel.mainQueryRequest.results.rows.slice(0, queryModel.effectiveVizLimit);

  const rowTokens = visualizationRows.map((row) =>
    getRowDimensionValues(row.get(), dimensions, dimensionFormatter).map((value, idx) =>
      splitPathDimensionValue(dimensions[idx].name, value)
    )
  );

  const columns = buildColumns(dimensions, rowTokens, measurement);
  const columnOffsets: number[] = [];
  let offset = 0;
  dimensions.forEach((dimension, idx) => {
    columnOffsets[idx] = offset;
    offset += isPathDimension(dimension.name)
      ? rowTokens.reduce((max, tokens) => Math.max(max, tokens[idx].length), 1)
      : 1;
  });

  const nodes = new Map<string, Highcharts.SeriesSankeyNodesOptionsObject>();
  const nodeDetails = new Map<string, { label?: string; value: string }>();
  const links: { from: string; to: string; weight: number; rowIds: string[] }[] = [];
  const chartedRowIds: string[] = [];

  const nodeId = (column: number, value: string) => `${column}\u0000${value}`;

  const labelPosition = (column: number): Highcharts.SeriesSankeyDataLabelsOptionsObject =>
    column < columns.length / 2
      ? { align: 'left', x: NODE_WIDTH + LABEL_OFFSET }
      : { align: 'right', x: -(NODE_WIDTH + LABEL_OFFSET) };

  const ensureNode = (column: number, value: string) => {
    const id = nodeId(column, value);
    if (!nodes.has(id)) {
      const dimensionName = columns[column]?.dimensionName ?? '';
      nodes.set(id, {
        id,
        name: shortenDimensionValue(dimensionName, value),
        column,
        dataLabels: labelPosition(column)
      });
      nodeDetails.set(id, { label: columns[column]?.label, value });
    }
    return id;
  };

  visualizationRows.forEach((row, rowIdx) => {
    const weight = row.rollups[rollupName];
    if (weight == null || !(weight > 0)) {
      return;
    }

    const path = rowTokens[rowIdx].flatMap((tokens, dimensionIdx) =>
      tokens.map((value, hop) => ({ column: columnOffsets[dimensionIdx] + hop, value }))
    );

    let linked = false;
    for (let i = 0; i < path.length - 1; i += 1) {
      const source = path[i];
      const target = path[i + 1];

      if (target.column > source.column) {
        const fromId = ensureNode(source.column, source.value);
        const toId = ensureNode(target.column, target.value);

        links.push({ from: fromId, to: toId, weight, rowIds: [row.id] });
        linked = true;
      }
    }

    if (linked) {
      chartedRowIds.push(row.id);
    }
  });

  const prefix = getDatasetPrefix(
    links.map((link) => link.weight),
    unit
  );
  const format = (value: number | undefined) => formatMetricValue(value, unit, { forcePrefix: prefix }).displayFull;

  const setPathState = (point: SankeyPoint, state: 'hover' | '') => {
    const incident = point.isNode ? [...(point.linksFrom ?? []), ...(point.linksTo ?? [])] : [point];
    const rowIds = new Set(incident.flatMap((link) => (link as SankeyPoint).custom?.rowIds ?? []));
    if (!rowIds.size) {
      return;
    }
    point.series.points.forEach((link) => {
      if (link.custom?.rowIds?.some((id) => rowIds.has(id))) {
        link.setState(state);
      }
    });
  };

  const options: Highcharts.Options = {
    chart: { type: 'sankey' },
    colors: queryModel.colors,
    legend: { enabled: false },
    tooltip: {
      shared: false,
      followPointer: true,
      useHTML: true,
      outside: true
    },
    plotOptions: {
      sankey: {
        nodeWidth: NODE_WIDTH,
        minLinkWidth: MIN_LINK_WIDTH,
        linkOpacity: LINK_OPACITY,
        states: { inactive: { enabled: false }, hover: { linkOpacity: LINK_HOVER_OPACITY } },
        dataLabels: {
          allowOverlap: true,
          crop: false,
          overflow: 'allow',
          verticalAlign: 'middle',
          style: { textOutline: 'none', color: theme.colors.body, fontWeight: 'normal', pointerEvents: 'none' }
        },
        point: {
          events: {
            mouseOver() {
              setPathState(this as SankeyPoint, 'hover');
            },
            mouseOut() {
              setPathState(this as SankeyPoint, '');
            }
          }
        },
        tooltip: {
          headerFormat: '',
          nodeFormatter(this: SankeyPoint) {
            const detail = nodeDetails.get(this.id ?? '');
            return `${detail?.label ? `<div>${detail.label}</div>` : ''}<div><b>${
              detail?.value ?? this.name
            }</b></div><div>${format(this.sum)}</div>`;
          },
          pointFormatter(this: SankeyPoint) {
            const from = nodeDetails.get(this.from ?? '')?.value;
            const to = nodeDetails.get(this.to ?? '')?.value;
            return `<div>${from} \u2192 ${to}</div><div><b>${format(this.weight)}</b></div>`;
          }
        }
      }
    },
    series: [
      {
        type: 'sankey',
        name: measurementMetric?.Label || metricName,
        nodes: [...nodes.values()],
        data: links.map((link) => ({
          from: link.from,
          to: link.to,
          weight: link.weight,
          color: theme.colors.muted,
          custom: { rowIds: link.rowIds }
        }))
      }
    ]
  };

  return {
    options: merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: { [queryModel.mainQueryRequest.type]: chartedRowIds }
  };
}
